import type {
  CallInfo,
  CreateCallRequest,
  CreateCallResponse,
  GetCaseRecordCallsResponse,
  UpdateCallRequest,
  UpdateCallResponse,
} from '@compass/client/types/call';
import { callService } from '@services/api';
import { useStore } from '@store/store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import type { TabItem } from '@/components/CallsTabs/types';
import type { OrgProgramContextOverride } from '@/types/context';
import { determineReviewTabSelection } from '@/utils/reviewTabSelection';

// Sort calls by scheduled_at (falling back to created_at) in ascending order (oldest first)
const sortCallsByStartedAt = (calls: CallInfo[]): CallInfo[] => {
  return [...calls].sort((a, b) => {
    const aTime =
      a.scheduled_at || a.created_at
        ? new Date((a.scheduled_at || a.created_at)!).getTime()
        : Number.MAX_SAFE_INTEGER;
    const bTime =
      b.scheduled_at || b.created_at
        ? new Date((b.scheduled_at || b.created_at)!).getTime()
        : Number.MAX_SAFE_INTEGER;

    return aTime - bTime;
  });
};

/**
 * Canonical React Query key for a single call. This is the SINGLE SOURCE OF TRUTH
 * for the key shape — `useCall` (the reader), `useUpdateCall` (optimistic
 * update/rollback/invalidate), and any out-of-hook cache access (e.g. GlobalDialpad's
 * post-call disposition save) MUST build the key through this helper. A `getQueryData`
 * or `invalidateQueries` with a differently-shaped key silently targets a nonexistent
 * entry (getQueryData → undefined, invalidate → no-op), which previously caused the
 * dialpad to read an empty base and wipe captured output_data on save.
 */
export const getCallQueryKey = (
  organizationId: string | null | undefined,
  programId: string | null | undefined,
  caseRecordId: string | null | undefined,
  callId: string | null | undefined,
  testModeEnabled: boolean,
) => ['call', organizationId, programId, caseRecordId, callId, testModeEnabled];

export const useCaseRecordCalls = (
  caseRecordId: string | null,
  options?: {
    refetchInterval?: number | false;
    contextOverride?: OrgProgramContextOverride;
  },
) => {
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const effectiveOrganizationId =
    options?.contextOverride?.organizationId || selectedOrganizationId;
  const effectiveProgramId = options?.contextOverride?.programId || selectedProgramId;

  const isEnabled = useMemo(() => {
    return !!caseRecordId && !!effectiveOrganizationId && !!effectiveProgramId && !testModeEnabled;
  }, [caseRecordId, effectiveOrganizationId, effectiveProgramId, testModeEnabled]);

  return useQuery({
    queryKey: [
      'caseRecordCalls',
      effectiveOrganizationId,
      effectiveProgramId,
      caseRecordId,
      testModeEnabled,
    ],
    queryFn: async () => {
      // Return empty response if in test mode since calls are not supported in sandbox
      if (testModeEnabled) {
        return { calls: [], total: 0 };
      }

      const response = await callService.getCaseRecordCalls({
        organization_id: effectiveOrganizationId!,
        program_id: effectiveProgramId!,
        caserecord_id: caseRecordId!,
      });

      // Return new object with sorted calls if they exist
      if (response.calls && response.calls.length > 0) {
        return {
          ...response,
          calls: sortCallsByStartedAt(response.calls),
        };
      }

      return response;
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - same as global default
    gcTime: 10 * 60 * 1000, // 10 minutes - same as global default
    refetchInterval: options?.refetchInterval,
  });
};

export const useCall = (
  caseRecordId: string | null,
  callId: string | null,
  options?: {
    refetchInterval?: number | false;
    contextOverride?: OrgProgramContextOverride;
  },
) => {
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);
  const effectiveOrganizationId =
    options?.contextOverride?.organizationId || selectedOrganizationId;
  const effectiveProgramId = options?.contextOverride?.programId || selectedProgramId;

  const isEnabled = useMemo(() => {
    return (
      !!caseRecordId &&
      !!effectiveOrganizationId &&
      !!effectiveProgramId &&
      !!callId &&
      !testModeEnabled
    );
  }, [caseRecordId, effectiveOrganizationId, effectiveProgramId, callId, testModeEnabled]);

  return useQuery({
    queryKey: getCallQueryKey(
      effectiveOrganizationId,
      effectiveProgramId,
      caseRecordId,
      callId,
      testModeEnabled,
    ),
    queryFn: async () => {
      // Return empty response if in test mode since calls are not supported in sandbox
      if (testModeEnabled) {
        return { call: null };
      }
      return callService.getCall({
        organization_id: effectiveOrganizationId!,
        program_id: effectiveProgramId!,
        caserecord_id: caseRecordId!,
        call_id: callId!,
      });
    },
    enabled: isEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - same as global default
    gcTime: 10 * 60 * 1000, // 10 minutes - same as global default
    refetchInterval: options?.refetchInterval,
  });
};

export const useCreateCall = () => {
  const queryClient = useQueryClient();
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation<CreateCallResponse, Error, CreateCallRequest>({
    mutationFn: async (data: CreateCallRequest) => {
      if (testModeEnabled) {
        throw new Error('Calls are not supported in test mode');
      }
      // user_id is intentionally not sent: the server stamps it from the
      // authenticated caller and ignores any client-supplied value.
      return callService.createCall({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        ...data,
      });
    },
    onSuccess: (data, variables) => {
      const newCall = data.call;
      if (!variables.caserecord_id || !newCall?.id) return;

      // Surface the just-created call IMMEDIATELY, instead of waiting up to the 30s calls-list
      // poll, so the Human Call Script tab (gated on the latest call being a live human call)
      // appears the instant the dial returns. The create response is the full call record, so
      // we seed both caches from it.
      const listKey = [
        'caseRecordCalls',
        selectedOrganizationId,
        selectedProgramId,
        variables.caserecord_id,
        testModeEnabled,
      ];
      // 1) Optimistically add it to the calls list → latestCallId / liveHumanCall /
      //    callScriptAvailable resolve this render (the new call is CALL_TYPE_HUMAN + NOT_STARTED,
      //    i.e. an active call). setQueryData is exact-match, so the key includes testMode.
      queryClient.setQueryData<GetCaseRecordCallsResponse>(listKey, old => {
        const calls = old?.calls ?? [];
        if (calls.some(c => c.id === newCall.id)) return old; // a poll may have beaten us
        return { ...old, calls: [...calls, newCall], total: (old?.total ?? calls.length) + 1 };
      });
      // 2) Seed the single-call cache so the script's useCall(latestCallId) has data with no
      //    extra round-trip (built via the canonical helper so it matches useCall's queryKey
      //    in the normal in-context case).
      queryClient.setQueryData(
        getCallQueryKey(
          selectedOrganizationId,
          selectedProgramId,
          variables.caserecord_id,
          newCall.id,
          testModeEnabled,
        ),
        { call: newCall },
      );
      // 3) Reconcile with the server. NOTE: the previous key (`['caseRecordCalls', caserecord_id]`)
      //    did NOT match the real query key (org/program precede the case id), so the invalidation
      //    silently no-op'd and the list only refreshed on its 30s poll. Use the correct prefix.
      queryClient.invalidateQueries({
        queryKey: [
          'caseRecordCalls',
          selectedOrganizationId,
          selectedProgramId,
          variables.caserecord_id,
        ],
      });
    },
  });
};

export const useUpdateCall = () => {
  const queryClient = useQueryClient();
  const testModeEnabled = useStore(state => state.testModeEnabled);
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  // Must match useCall's queryKey (prefix), or invalidation and the optimistic
  // setQueryData/rollback silently target a nonexistent cache entry. NOTE: this uses
  // the store's selected org/program, so it matches useCall in the normal in-context
  // case but NOT when useCall is built from a contextOverride (e.g. QC cross-org
  // views) — there the optimistic update/rollback/invalidation no-op.
  const callQueryKey = (caseRecordId: string, callId: string) =>
    getCallQueryKey(
      selectedOrganizationId,
      selectedProgramId,
      caseRecordId,
      callId,
      testModeEnabled,
    );

  return useMutation<
    UpdateCallResponse,
    Error,
    {
      caseRecordId: string;
      callId: string;
      data: UpdateCallRequest;
    },
    { previousCall: unknown }
  >({
    mutationFn: async ({
      caseRecordId,
      callId,
      data,
    }: {
      caseRecordId: string;
      callId: string;
      data: UpdateCallRequest;
    }) => {
      if (testModeEnabled) {
        throw new Error('Calls are not supported in test mode');
      }
      return callService.updateCall({
        organization_id: selectedOrganizationId!,
        program_id: selectedProgramId!,
        caserecord_id: caseRecordId,
        call_id: callId,
        ...data,
      });
    },
    onMutate: async ({ caseRecordId, callId, data }) => {
      const key = callQueryKey(caseRecordId, callId);
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: key });

      // Snapshot the previous value
      const previousCall = queryClient.getQueryData(key);

      // Optimistically update to the new value
      queryClient.setQueryData(key, (old: any) => ({
        ...old,
        call: {
          ...old?.call,
          ...(data.disposition && { disposition: data.disposition }),
          ...(data.disposition_type && { disposition_type: data.disposition_type }),
          ...(data.input_data && { input_data: data.input_data }),
          ...(data.output_data && { output_data: data.output_data }),
          ...(data.heard_markers && { heard_markers: data.heard_markers }),
          ...(data.chart_info && {
            chart_info: { ...old?.call?.chart_info, ...data.chart_info },
          }),
        },
      }));

      // Return a context object with the snapshotted value
      return { previousCall };
    },
    onError: (_err, variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousCall) {
        queryClient.setQueryData(
          callQueryKey(variables.caseRecordId, variables.callId),
          context.previousCall,
        );
      }
    },
    onSettled: (_data, _error, variables) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({
        queryKey: callQueryKey(variables.caseRecordId, variables.callId),
      });
      // The calls LIST carries each call's disposition too, and page-level
      // guards read it from there (getNewCallBlockReason disabling the Call
      // button, getHumanCallCompletionBlockReason gating the Confirm dialog).
      // Without this, saving a disposition left both stale until the list's 30s
      // poll came round. Same key prefix useCreateCall invalidates — org and
      // program precede the case id, so a shorter prefix silently no-ops.
      queryClient.invalidateQueries({
        queryKey: [
          'caseRecordCalls',
          selectedOrganizationId,
          selectedProgramId,
          variables.caseRecordId,
        ],
      });
    },
  });
};

/**
 * Hook to manage automatic tab selection for Review tab (calls and eligibility checks)
 * - Default: Selects the newest (last) tab
 * - Manual click: Tracked via `manuallySelectedCallId` in store
 * - On render: If `manuallySelectedCallId` exists and is valid for this case, use it; otherwise use last tab
 */
export const useReviewTabSelection = (unifiedTabItems: TabItem[] | undefined) => {
  const { selectedCallId, setSelectedCallId, manuallySelectedCallId } = useStore();

  useEffect(() => {
    const targetId = determineReviewTabSelection(unifiedTabItems, manuallySelectedCallId);

    if (targetId && targetId !== selectedCallId) {
      setSelectedCallId(targetId);
    }
  }, [unifiedTabItems, manuallySelectedCallId, selectedCallId, setSelectedCallId]);
};
